import {
  BookingLifecycleBucket,
  bookingLifecycleBucket,
} from "@specsheet-common/shared-types";
import { DateTime } from "luxon";
import { DATABASE_TIMEZONE } from "./dateHelpers";

/** A row's live/end dates as `yyyy-MM-dd` calendar days (null = unknown). */
export type LiveEndDays = { live: string | null; end: string | null };

/**
 * Bucket for a single live/end pair. Calendar-day strings compare lexically.
 *
 * - live after today                        → upcoming
 * - live today or earlier, end before today → completed
 * - live today or earlier otherwise         → live (end today, later, or missing)
 * - no live, end before today               → completed
 * - no live, end today                      → live
 * - no live, end after today                → upcoming
 * - neither                                 → undated
 * - live after end (data error)             → judged by live alone, so it stays visible
 */
export function classifyLiveEndDays(
  { live, end }: LiveEndDays,
  today: string
): BookingLifecycleBucket {
  const effectiveEnd = live && end && live > end ? null : end;

  if (live) {
    if (live > today) {
      return bookingLifecycleBucket.upcoming;
    }
    if (effectiveEnd && effectiveEnd < today) {
      return bookingLifecycleBucket.completed;
    }
    return bookingLifecycleBucket.live;
  }

  if (effectiveEnd) {
    if (effectiveEnd < today) {
      return bookingLifecycleBucket.completed;
    }
    if (effectiveEnd === today) {
      return bookingLifecycleBucket.live;
    }
    return bookingLifecycleBucket.upcoming;
  }

  return bookingLifecycleBucket.undated;
}

/**
 * Bucket for a whole row from its sub-row buckets: completed only when every
 * dated sub-row has ended, upcoming only when none has started, otherwise
 * live. Undated sub-rows are ignored unless every sub-row is undated.
 */
export function combineLifecycleBuckets(
  buckets: BookingLifecycleBucket[]
): BookingLifecycleBucket {
  const dated = buckets.filter((b) => b !== bookingLifecycleBucket.undated);

  if (dated.length === 0) {
    return bookingLifecycleBucket.undated;
  }
  if (dated.every((b) => b === bookingLifecycleBucket.completed)) {
    return bookingLifecycleBucket.completed;
  }
  if (dated.every((b) => b === bookingLifecycleBucket.upcoming)) {
    return bookingLifecycleBucket.upcoming;
  }
  return bookingLifecycleBucket.live;
}

/** Contract date (a `date` column, serialised at UTC midnight) → calendar day. */
export function contractDateToCalendarDay(
  value: Date | string | null | undefined
): string | null {
  if (!value) {
    return null;
  }
  if (typeof value === "string") {
    return value.split("T")[0];
  }
  return DateTime.fromJSDate(value, { zone: "utc" }).toISODate();
}

/**
 * Template DATE field value (epoch ms stored with Sydney wall-clock semantics)
 * → the calendar day the user picked.
 */
export function templateDateToCalendarDay(
  ms: number | null | undefined
): string | null {
  if (ms === null || ms === undefined || !Number.isFinite(ms)) {
    return null;
  }
  return DateTime.fromMillis(ms).setZone(DATABASE_TIMEZONE).toISODate();
}

/** Today's calendar day in the given IANA zone (defaults to the database zone). */
export function todayCalendarDayIn(zone?: string): string {
  return (
    DateTime.now()
      .setZone(zone ?? DATABASE_TIMEZONE)
      .toISODate() ?? ""
  );
}

/**
 * Resolve the per-sub-row live/end days for a booking: template Live Date /
 * End Date values win per side, contract dates are the fallback.
 */
export function resolveLiveEndDays({
  contractLive,
  contractEnd,
  templateRows,
}: {
  contractLive: string | null;
  contractEnd: string | null;
  /** One entry per sub-row: the template live/end calendar days, if any. */
  templateRows: Array<{ live: string | null; end: string | null }>;
}): LiveEndDays[] {
  if (templateRows.length === 0) {
    return [{ live: contractLive, end: contractEnd }];
  }
  return templateRows.map((row) => ({
    live: row.live ?? contractLive,
    end: row.end ?? contractEnd,
  }));
}

/** Convenience: bucket for a booking from resolved days. */
export function lifecycleBucketFor(
  input: Parameters<typeof resolveLiveEndDays>[0],
  today: string
): BookingLifecycleBucket {
  return combineLifecycleBuckets(
    resolveLiveEndDays(input).map((pair) => classifyLiveEndDays(pair, today))
  );
}
